import React, { useCallback, useEffect, useState } from "react";
import { Avatar, CircularProgress, Stack, Typography } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";

import { useAllowDealTabOpenSelector, useDealActions } from "store";
import { TooltipDiv } from "components/tooltips/TooltipDiv";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { IDealPreview, IGetDealsData } from "store/types";

export const SharedList: React.FC = () => {
  const [data, setData] = useState<IDealPreview[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const { getDealsCardsAction } = useDealActions();
  const allowNewTab = useAllowDealTabOpenSelector();

  const getDeal = useCallback(
    async (data: IGetDealsData & { turnOnLoading?: boolean }) => {
      try {
        if (data.turnOnLoading) {
          setLoading(true);
        }
        const { data: deals, last_page } = await getDealsCardsAction(data);
        if (data.page! >= last_page) {
          setHasMore(false);
        }

        setPage((prevState) => prevState + 1);
        setData((prevState) => [...prevState, ...deals]);
      } finally {
        if (data.turnOnLoading) {
          setLoading(false);
        }
      }
    },
    [setPage, hasMore]
  );

  // Initial API call
  useEffect(() => {
    getDeal({
      page: 1,
      turnOnLoading: true
    });
  }, []);

  return (
    <AsyncContentMui isLoading={loading} spinnerProps={{ size: 100 }}>
      <InfiniteScroll
        loader={
          <Stack height="100%" justifyContent="center" alignItems="center">
            <CircularProgress size={150} />
          </Stack>
        }
        next={() => getDeal({ page })}
        height="100%"
        hasMore={hasMore}
        dataLength={data.length}
      >
        <Stack direction="row" sx={{ gap: "16px", pr: "16px", flexWrap: "wrap", overflow: "auto" }}>
          {data.map((item) => (
            <NavigateLink key={item.id} allowNewTab={allowNewTab} route="deal" params={{ deal_id: String(item.id) }}>
              <Stack
                sx={{
                  width: 240,
                  height: 110,
                  p: "12px",
                  borderRadius: "8px",
                  bgcolor: "white",
                  boxShadow: "sm",
                  flexShrink: 0,
                  cursor: "pointer"
                }}
              >
                <Stack
                  sx={{
                    borderRadius: "8px",
                    bgcolor: "#FBE3D3",
                    p: "8px",
                    flexGrow: 1,
                    justifyContent: "space-between",
                    overflow: "hidden"
                  }}
                >
                  <Stack>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        lineHeight: "24px",
                        fontWeight: 400,
                        textOverflow: "ellipsis",
                        overflow: "hidden"
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", lineHeight: "16px", color: "#496682" }}>
                      {item.asset_class.presentation_name}
                    </Typography>
                  </Stack>
                  <Stack direction="row" sx={{ justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: "12px", color: "#496682", lineHeight: "16px" }}>
                      {item.deal_role}
                    </Typography>
                    {item.inviter && (
                      <TooltipDiv place="bottom" tooltipMessage={item.inviter.full_name}>
                        <Avatar sizes="16px" sx={{ width: 16, height: 16 }} src={item.inviter.profile_photo_url} />
                      </TooltipDiv>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </NavigateLink>
          ))}
        </Stack>
      </InfiniteScroll>
    </AsyncContentMui>
  );
};
